import * as http from "utils/http";
import * as notifications from "utils/notifications";
import { parseErrorFromJson } from "utils/responses";
import { BaseRefundRequestModalController } from "./base_refund_request_modal_controller";
import { getRefundRequestUpdateModalData, removeRefundRequestUpdateModalData } from "./refund_requests_controller";
import { RefundRequest } from "../../types/refund_request";

export default class extends BaseRefundRequestModalController<RefundRequest> {
  static targets = ["reasonInput", "cancelButton", "submitButton", "form"];
  static values = {
    refundRequestId: String,
    actionType: String,
    requestedAmount: Number,
  };

  declare readonly reasonInputTarget: HTMLTextAreaElement;
  declare readonly cancelButtonTarget: HTMLButtonElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly formTarget: HTMLFormElement;
  declare refundRequestIdValue: string;
  declare actionTypeValue: string;
  declare requestedAmountValue: number;

  connect(): void {
    const modalData = getRefundRequestUpdateModalData(this.refundRequestIdValue);
    this.initializeModal(modalData);
    this.setupFocusTrap(this.reasonInputTarget);
    this.setupEscapeKeyHandler();

    if (this.reasonInputTarget) {
      this.reasonInputTarget.focus();
    }
  }

  cancel(): void {
    this.handleCancel();
  }

  closeOnOverlayClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target === this.element || target.classList.contains("modal-overlay")) {
      this.handleCancel();
    }
  }

  protected handleCancel(): void {
    this.restoreActions();
    this.closeModal();
  }

  private getFormData(): { update_reason: string | null } {
    const reasonValue = this.reasonInputTarget.value.trim();
    const update_reason = reasonValue === "" ? null : reasonValue;

    return { update_reason };
  }

  private buildRequestBody(formData: { update_reason: string | null }): {
    refund_request_update: { type: string; amount: number; update_reason?: string };
  } {
    const body: { refund_request_update: { type: string; amount: number; update_reason?: string } } = {
      refund_request_update: {
        type: this.actionTypeValue,
        amount: this.requestedAmountValue,
      },
    };

    if (formData.update_reason) {
      body.refund_request_update.update_reason = formData.update_reason;
    }

    return body;
  }

  private setButtonLoading(): void {
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.textContent = `${this.actionTypeValue === "accept" ? "Accepting" : "Rejecting"}...`;
  }

  private setButtonReady(): void {
    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.textContent = this.actionTypeValue === "accept" ? "Accept" : "Reject";
  }

  private updateGridAfterSuccess(data: { new_status: string }): void {
    if (!this.node) return;

    this.node.setDataValue("status_code", data.new_status);
    this.node.setDataValue("status", data.new_status);
    this.node.setDataValue("actions", "");
    this.refreshGridCell(this.node, "actions");
    this.refreshGridCell(this.node, "status");

    if (this.gridApi) {
      window.setTimeout(() => this.gridApi?.purgeInfiniteCache(), 1000);
    }
  }

  private handleError(errorMessage: string): void {
    this.setButtonReady();
    super.handleError(errorMessage);
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();

    const formData = this.getFormData();
    this.setButtonLoading();

    try {
      const body = this.buildRequestBody(formData);
      const response = await http.httpPatch(`/payments/refund_requests/${this.refundRequestIdValue}`, body);
      const data = await response.json();

      if (response.ok) {
        notifications.success(data.message);
        this.updateGridAfterSuccess(data);
        this.requestCompleted = true;
        removeRefundRequestUpdateModalData(this.refundRequestIdValue);
        this.closeModal();
      } else {
        const errorMessage = data.error
          ? parseErrorFromJson(data.error)
          : data.message
            ? parseErrorFromJson(data.message)
            : `Failed to ${this.actionTypeValue} refund request`;
        this.handleError(errorMessage);
      }
    } catch (error) {
      this.handleError(
        `An error occurred while ${this.actionTypeValue === "accept" ? "accepting" : "rejecting"} refund request`,
      );
    }
  }

  override disconnect(): void {
    super.disconnect();
    if (!this.requestCompleted) {
      removeRefundRequestUpdateModalData(this.refundRequestIdValue);
    }
  }
}
